/**
 * The **Peer Configuration** dialog (ADR-039 §D-1; storyboard Flow W;
 * REQ-UI-139..-147).
 *
 * A modal reached from the System Status display's header affordance and from
 * the Peer Manager node's detail pane, following the disable-aggregator
 * precedent of a mutation control embedded in that panel — but a **live
 * control** rather than an audit-only signal. One surface covers every peer and
 * everything beneath it, while the diagram carries the live *status*:
 * configuration is one thing an operator manages, and health is many things they
 * watch.
 *
 * # Three levels, one pane (REQ-UI-141)
 *
 * The roster lists **peers**. Selecting one opens a single scrollable pane with
 * sticky section headers: the peer's own fields, then its interfaces, each
 * expanding in place into its editor and that editor's connections. No sub-tabs
 * (ADR-024 §4) — sub-tabs would hide two of the three levels at all times, and
 * this surface is opened as often to *stop* something as to configure it.
 *
 * Peer is the level the operator reasons at. "Stop everything going to the
 * fusion cell" is one act here and an unknown number one level down — and before
 * Phase J the operator had to already know which interfaces served that cell to
 * perform it, which is precisely the knowledge the roster supplies.
 *
 * # Master–detail, not a wizard
 *
 * Flow G's aggregator onboarding is a stepped wizard because it provisions one
 * thing once. This surface opens on the roster with every peer's state visible
 * at a glance; creating walks the same pane top to bottom in the storyboard's
 * order (add peer → add interface → choose type → configure connections → define
 * filter → install trust → start). A wizard would put four clicks between an
 * operator and a kill switch.
 *
 * # RBAC (feature-spec §2.10 / Flow L §1)
 *
 * Every authenticated role opens this surface and reads it, field-limited by the
 * BFF. For non-privileged roles the mutation controls are **absent, not
 * disabled** (REQ-UI-143) — a disabled Start button tells an operator the system
 * has a capability they may not use, which on this surface is an invitation to
 * go looking for a way to use it. The BFF enforces regardless; this hiding is
 * cosmetic (ADR-009 defence-in-depth), and the client never re-derives the field
 * split from its session roles.
 *
 * That rule governs *permissions only*. A control the operator is entitled to
 * but which the surface cannot perform stays visible and disabled with an
 * explanation — which is what `Add interface` was between Phases E and J, and
 * why it is live again here.
 *
 * # Nothing is fetched until it is opened (REQ-UI-147)
 *
 * The roster query is gated on `open`, and Radix unmounts this body on close.
 * Peer configuration changes only when an operator changes it, so fetching it on
 * mount would put a request on every session for a surface most never open.
 *
 * Cross-references:
 *   - ADR-039 §D-1; feature-spec §2.10 / §2.11 / §3.4 / §5.1.
 *   - REQ-UI-139..-147; REQ-PEER-055 / -057 / -061 / -063 / -067 / -078.
 *   - Flow L §1 (the entry points + role gating); Flow W (this surface).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useMemo, useState } from "react";
import { CREATABLE_INTERFACE_TYPES, type InterfaceState } from "@/peers/peer-catalog";
import { PeerEditor } from "@/peers/peer-editor";
import { PeerInterfacesSection } from "@/peers/peer-interfaces-section";
import { PeerRoster } from "@/peers/peer-roster";
import {
  isPrivilegedPeerView,
  newInterfaceConfig,
  newPeerConfig,
  toPeerConfig,
  type PeerConfig,
  type PeerInterfaceConfig,
} from "@/peers/peer-model";
import { PEER_MUTATOR_ROLES, usePeerInterfaces } from "@/peers/use-peer-interfaces";
import { PeerImportDialog } from "@/peers/peer-import-dialog";
import {
  useDeleteNestedConnection,
  useDeleteNestedInterface,
  useDeletePeer,
  useExportPeerBundle,
  usePeerInterfacesFor,
  usePeers,
  useSaveNestedInterface,
  useSavePeer,
  useSetNestedConnectionState,
  useSetNestedInterfaceState,
  useSetPeerState,
} from "@/peers/use-peers";
import { useSession } from "@/auth/session-context";
import { DescriptionTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * What the Peers dialog is for, disclosed from its title.
 *
 * Was a header subtitle. Kept as reachable text rather than deleted: "peer" is
 * Meridian's own term for an external system this deployment sends data to
 * (ADR-039 generalized it over the consumer and CDS-send kinds), so the first
 * operator to open this needs it and every one after does not.
 */
const PEER_DIALOG_HELP =
  "The external systems this deployment sends data to. Each peer owns one or more " +
  "interfaces, and each interface one or more connections.";

/** Props for {@link PeerManagementDialog}. */
export interface PeerManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pre-select a peer by name, or by the name of an interface it owns — the
   * entity the operator came from on the System display.
   *
   * **Either level, resolved peer-first** (ADR-039 Phase I). The diagram's icon
   * stack used to carry interface names and now carries *peer* names
   * (REQ-STATUS-081), while the Selection Detail's rosters can hand over either.
   * Rather than split this into two props — which would let a caller pass the
   * right name on the wrong one and land silently on the first peer — one name
   * is matched against peers first and against interfaces second. Peer names and
   * interface names are each unique deployment-wide, so the only ambiguity is a
   * peer and an interface sharing a name (`cds`/`cds`), where the peer wins:
   * that is the level this dialog's roster is keyed by.
   *
   * When nothing matches (a name from a newer build, or an entity just deleted)
   * the dialog falls back to the first peer rather than rendering an empty
   * detail.
   */
  initialSelectionName?: string | null;
  /** Mints ids for new peers / interfaces / connections; injectable for tests. */
  makeId?: () => string;
}

/** Render the peer configuration dialog. */
export function PeerManagementDialog({
  open,
  onOpenChange,
  initialSelectionName = null,
  makeId = () => crypto.randomUUID(),
}: PeerManagementDialogProps): React.ReactElement {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-testid="peer-management-dialog"
          aria-describedby={undefined}
          // Focus the dialog container on open, not its first tabbable child.
          //
          // Radix's default is "focus the first tabbable element", and as of
          // 2026-10-10 that is the title's help trigger — a Radix Tooltip
          // trigger, which opens on focus. Left to the default, the
          // explanatory tooltip would pop open every single time an operator
          // opened this dialog, which is the opposite of the reason the prose
          // was moved into a tooltip in the first place. (Before that change
          // the default landed on the Close button, which is its own small
          // hazard: an accidental Enter on an opening dialog closed it.)
          //
          // Focusing the container is the conventional modal pattern — the
          // dialog is announced, Escape works, and Tab then walks the contents
          // in document order — and it leaves the tooltip's focus-to-open
          // behaviour intact for the keyboard user who actually tabs to it.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (event.currentTarget instanceof HTMLElement) {
              event.currentTarget.focus();
            }
          }}
          // `system-status-scope` is applied deliberately: this modal is the
          // System Status panel's mutation surface and needs exactly that
          // panel's status tokens (the legible standby grey — `--status-stale`
          // is otherwise 1.38:1 on a light panel). Radix portals to <body>, so
          // the class has to travel with the content.
          className={cn(
            "system-status-scope fixed left-1/2 top-1/2 z-50 flex h-[80vh] w-[1080px] max-w-[95vw]",
            "-translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-md border",
            "border-border bg-bg-surface shadow-lg",
          )}
        >
          <DialogBody
            open={open}
            initialSelectionName={initialSelectionName}
            makeId={makeId}
            onClose={() => onOpenChange(false)}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The selection: an existing peer id, a pending create, or nothing. */
type Selection = { kind: "existing"; id: string } | { kind: "new" } | null;

/** The dialog's content — split out so it mounts only while open (REQ-UI-147). */
function DialogBody({
  open,
  initialSelectionName,
  makeId,
  onClose,
}: {
  open: boolean;
  initialSelectionName: string | null;
  makeId: () => string;
  /** Dismiss the whole surface — what the editor's Cancel does. */
  onClose: () => void;
}): React.ReactElement {
  const session = useSession();
  const mayMutate = session.hasAnyRole(PEER_MUTATOR_ROLES);
  const peersQuery = usePeers(open);
  const peers = useMemo(() => peersQuery.data ?? [], [peersQuery.data]);

  const [requested, setRequested] = useState<Selection>(null);
  const [draftPeerId, setDraftPeerId] = useState<string | null>(null);
  const [expandedInterfaceId, setExpandedInterfaceId] = useState<string | null>(null);
  const [interfaceDraft, setInterfaceDraft] = useState<PeerInterfaceConfig | null>(null);

  const savePeer = useSavePeer();
  const deletePeer = useDeletePeer();
  const setPeerState = useSetPeerState();
  const saveInterface = useSaveNestedInterface();
  const deleteInterface = useDeleteNestedInterface();
  const setInterfaceState = useSetNestedInterfaceState();
  const setConnectionState = useSetNestedConnectionState();
  const deleteConnection = useDeleteNestedConnection();
  const exportPeer = useExportPeerBundle();
  // Import is roster-level, not peer-level: it CREATES a peer, so it cannot be
  // reached from a peer's own section (REQ-UI-150).
  const [importOpen, setImportOpen] = useState(false);

  /**
   * Resolve the handed-over name to a peer id — **peers first, interfaces
   * second** (see :prop:`initialSelectionName`).
   *
   * The peer match is free: the roster is already loaded above, so a handover
   * from the diagram's peer stack or the node's peer list costs no request at
   * all. The interface fallback is the one that costs, and it is issued only
   * while the dialog is open and only when the peer match missed.
   *
   * ⚠ The System Status panel used to hold the flat interface list open for its
   * own icon stack, which made this a cache hit; since ADR-039 Phase I the stack
   * enumerates peers and the panel no longer fetches it, so on the interface
   * path this is a real request. That is the right trade — it is now paid only
   * by the surface that needs it, and only on a fallback.
   *
   * ``peer_id`` on the interface read is privileged (feature-spec §2.10), so for
   * an `operator` session the fallback resolves to ``null`` and the dialog lands
   * on the first peer. Degraded, not broken: a non-privileged reader can still
   * find the peer in the roster — and the peer-name path above needs no
   * privileged field at all, so the common case is no longer degraded for them.
   */
  const flatInterfaces = usePeerInterfaces(open && initialSelectionName !== null);
  const initialPeerId = useMemo(() => {
    if (initialSelectionName === null) return null;
    const peerMatch = peers.find((peer) => peer.name === initialSelectionName);
    if (peerMatch !== undefined) return peerMatch.peer_id;
    const match = (flatInterfaces.data ?? []).find((i) => i.name === initialSelectionName);
    return match?.peer_id ?? null;
  }, [initialSelectionName, peers, flatInterfaces.data]);

  /**
   * The selection actually rendered — **derived, not stored** (coding-standards
   * §2.5: prefer derived state over an effect).
   *
   * ``requested`` is only what the operator last clicked. Resolving it against
   * the live roster on every render is what makes the pane self-heal in the
   * three cases that otherwise strand it on an empty detail: the roster has not
   * arrived yet, the selected peer was just deleted, and — the one that showed
   * up in the Flow-W visual pass — the dialog was closed and reopened, which
   * unmounts Radix's content and resets ``requested`` while the query cache
   * still holds the list. A stored selection made "nothing selected" and "the
   * thing you selected is gone" render identically, which on this surface reads
   * as a peer that has vanished.
   */
  const selection: Selection = useMemo(() => {
    if (requested?.kind === "new") return requested;
    if (requested?.kind === "existing" && peers.some((p) => p.peer_id === requested.id)) {
      return requested;
    }
    const landing =
      (initialPeerId !== null ? peers.find((p) => p.peer_id === initialPeerId) : undefined) ??
      peers[0];
    return landing ? { kind: "existing", id: landing.peer_id } : null;
  }, [requested, peers, initialPeerId]);

  const selectedPeer =
    selection?.kind === "existing" ? (peers.find((p) => p.peer_id === selection.id) ?? null) : null;

  const interfacesQuery = usePeerInterfacesFor(selectedPeer?.peer_id ?? null);
  const interfaces = useMemo(() => interfacesQuery.data ?? [], [interfacesQuery.data]);

  const selectPeer = (peerId: string): void => {
    setRequested({ kind: "existing", id: peerId });
    // A different peer's interfaces are a different set; carrying an expansion
    // or a half-typed draft across would attach it to the wrong parent.
    setExpandedInterfaceId(null);
    setInterfaceDraft(null);
  };

  const startPeerCreate = (): void => {
    setDraftPeerId(makeId());
    setRequested({ kind: "new" });
    setExpandedInterfaceId(null);
    setInterfaceDraft(null);
  };

  const peerDraft: PeerConfig | null = useMemo(() => {
    if (selection?.kind === "new" && draftPeerId !== null) return newPeerConfig(draftPeerId);
    if (selectedPeer !== null && isPrivilegedPeerView(selectedPeer))
      return toPeerConfig(selectedPeer);
    return null;
  }, [selection, draftPeerId, selectedPeer]);

  return (
    <>
      <header className="flex items-center gap-3 border-b border-border px-4 py-2">
        {/* Titled for the control that opens it, not for the subsystem behind
            it: the operator clicked "Peers…" and must land somewhere that says
            so (architect, 2026-08-01). */}
        <Dialog.Title className="text-body font-medium text-text-primary">Peers</Dialog.Title>
        {/* The subtitle moved into the title's tooltip on 2026-10-10 (architect
            direction, with the rest of this dialog's standing prose). */}
        <DescriptionTooltip testId="peer-dialog" label="Peers" description={PEER_DIALOG_HELP} />
        <Dialog.Close asChild>
          <button
            type="button"
            aria-label="Close peers"
            data-testid="peer-dialog-close"
            className="ml-auto rounded p-1 text-text-secondary hover:bg-bg-elevated"
          >
            ✕
          </button>
        </Dialog.Close>
      </header>

      {/* Desktop-first (operator workstations): side-by-side master-detail. Below
          `md` the two stack instead, because a 280px roster beside a 1080px form
          in a 375px viewport clips the form off-screen with no way to reach it —
          "does not overflow the page" is not the same as "usable". */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <PeerRoster
          peers={peers}
          selectedId={selection?.kind === "existing" ? selection.id : null}
          onSelect={selectPeer}
          mayMutate={mayMutate}
          onCreate={startPeerCreate}
          onImport={() => setImportOpen(true)}
          creating={selection?.kind === "new"}
          isLoading={peersQuery.isLoading}
          error={peersQuery.isError ? peersQuery.error.message : null}
        />

        {/* ONE scrollable pane across all three levels (REQ-UI-141): the section
            headers stick against this scroller, and no child owns one of its own. */}
        <main className="min-h-0 flex-1 overflow-auto" data-testid="peer-detail">
          {selection === null ? (
            <p className="px-4 py-3 text-body-sm text-text-muted">
              Select a peer to see its configuration.
            </p>
          ) : (
            <>
              {mayMutate && peerDraft !== null && (
                <PeerEditor
                  // Remount on selection change so the form re-seeds cleanly.
                  key={selection.kind === "new" ? `new-${draftPeerId}` : selection.id}
                  value={peerDraft}
                  isNew={selection.kind === "new"}
                  interfaceCount={interfacesQuery.isSuccess ? interfaces.length : null}
                  otherNames={peers
                    .filter((p) => p.peer_id !== peerDraft.peer_id)
                    .map((p) => p.name)}
                  savePending={savePeer.isPending}
                  statePending={setPeerState.isPending}
                  saveError={savePeer.isError ? describePeerError(savePeer.error.status) : null}
                  onSave={(config) =>
                    savePeer.mutate(
                      { config },
                      { onSuccess: () => setRequested({ kind: "existing", id: config.peer_id }) },
                    )
                  }
                  onDelete={() =>
                    selection.kind === "existing" &&
                    deletePeer.mutate(selection.id, { onSuccess: () => setRequested(null) })
                  }
                  onSetState={(state: InterfaceState) =>
                    selection.kind === "existing" &&
                    setPeerState.mutate({ peerId: selection.id, state })
                  }
                  onExport={
                    selection.kind === "existing" && selectedPeer !== null
                      ? () =>
                          exportPeer.mutate({
                            peerId: selectedPeer.peer_id,
                            peerName: selectedPeer.name,
                          })
                      : undefined
                  }
                  exportPending={exportPeer.isPending}
                  exportError={
                    exportPeer.isError
                      ? "Could not export this peer. Nothing left the deployment."
                      : null
                  }
                />
              )}
              {!mayMutate && selectedPeer !== null && (
                <PeerReadOnlyHeader name={selectedPeer.name} />
              )}

              {selectedPeer !== null && (
                <PeerInterfacesSection
                  peer={{ name: selectedPeer.name, state: selectedPeer.state }}
                  interfaces={interfaces}
                  isLoading={interfacesQuery.isLoading}
                  error={interfacesQuery.isError ? interfacesQuery.error.message : null}
                  expandedId={expandedInterfaceId}
                  onExpand={setExpandedInterfaceId}
                  mayMutate={mayMutate}
                  onCreate={() =>
                    setInterfaceDraft(
                      newInterfaceConfig(makeId(), CREATABLE_INTERFACE_TYPES[0].typeId),
                    )
                  }
                  draft={interfaceDraft}
                  makeId={makeId}
                  savePending={saveInterface.isPending}
                  statePending={setInterfaceState.isPending || setConnectionState.isPending}
                  saveError={
                    saveInterface.isError
                      ? describeInterfaceError(saveInterface.error.status, saveInterface.error.body)
                      : null
                  }
                  onSave={(config) =>
                    saveInterface.mutate(
                      { peerId: selectedPeer.peer_id, config },
                      {
                        onSuccess: () => {
                          setInterfaceDraft(null);
                          setExpandedInterfaceId(config.interface_id);
                        },
                      },
                    )
                  }
                  onDelete={(interfaceId) =>
                    deleteInterface.mutate(
                      { peerId: selectedPeer.peer_id, interfaceId },
                      { onSuccess: () => setExpandedInterfaceId(null) },
                    )
                  }
                  onCancelDraft={() => setInterfaceDraft(null)}
                  onSetInterfaceState={(interfaceId, state) =>
                    setInterfaceState.mutate({ peerId: selectedPeer.peer_id, interfaceId, state })
                  }
                  onSetConnectionState={(interfaceId, connectionId, state) =>
                    setConnectionState.mutate({
                      peerId: selectedPeer.peer_id,
                      interfaceId,
                      connectionId,
                      state,
                    })
                  }
                  onDeleteConnection={(interfaceId, connectionId) =>
                    deleteConnection.mutate({
                      peerId: selectedPeer.peer_id,
                      interfaceId,
                      connectionId,
                    })
                  }
                />
              )}

              <footer className="flex items-center justify-end border-t border-border px-4 py-2">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="peer-close"
                  className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
                >
                  Close
                </button>
              </footer>
            </>
          )}
        </main>
      </div>

      {/* Mounted only while open, like the dialog itself (REQ-UI-147): parsing is
          local, but the component holds the operator's selected file and there is
          no reason for it to exist before they ask for it. */}
      {importOpen && (
        <PeerImportDialog
          onClose={() => setImportOpen(false)}
          onInstalled={(peerName) => {
            setImportOpen(false);
            // Land on what was just installed. The roster invalidation has been
            // issued but not necessarily settled, so this resolves by NAME
            // through the same path `initialSelectionName` uses rather than by an
            // id this client has never seen.
            const installed = peers.find((peer) => peer.name === peerName);
            if (installed !== undefined) setRequested({ kind: "existing", id: installed.peer_id });
          }}
        />
      )}
    </>
  );
}

/**
 * The peer's identity for a session that may not edit it.
 *
 * Deliberately not a disabled copy of {@link PeerEditor}: a greyed-out form of
 * fields the BFF never sent would be a shape suggesting there is something
 * behind it (REQ-UI-143). The peer's *name* is in the all-roles field-set and is
 * what labels the interfaces below, so it is stated plainly and nothing else is.
 */
function PeerReadOnlyHeader({ name }: { name: string }): React.ReactElement {
  return (
    <div className="border-b border-border px-4 py-3" data-testid="peer-read-only-header">
      <h3 className="text-body font-medium text-text-primary">{name}</h3>
    </div>
  );
}

/**
 * Map a peer save failure to operator-facing copy.
 *
 * Generic by design (REQ-UI-037 — no internal-error leakage), but specific where
 * the operator can act: a 409 is either a name they can change or a deployment
 * that is full, which are different next steps.
 */
function describePeerError(status: number): string {
  if (status === 409) return "Another peer already uses that name, or this deployment is full.";
  if (status === 422) return "The server rejected this peer. Check the fields above.";
  if (status === 403) return "This session may not change peer configuration.";
  return "Could not save the peer. The change was not applied.";
}

/**
 * The BFF's typed refusals carry a `message` written for this surface (REQ-UI-037:
 * no internals, the operator's next step named) — the published-listener-range
 * refusal (REQ-PROVIDER-057 / REQ-PEER-114) names the connection, the port, the
 * range and the `.env` key to widen. Shown verbatim where present; a 422 whose
 * body is pydantic's field list is not typed and falls through to the generic copy.
 */
function typedRefusalMessage(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail !== "object" || detail === null) return null;
  const message = (detail as { message?: unknown }).message;
  return typeof message === "string" && message !== "" ? message : null;
}

/** Map an interface save failure to operator-facing copy. See {@link describePeerError}. */
function describeInterfaceError(status: number, body?: unknown): string {
  if (status === 422) {
    const typed = typedRefusalMessage(body);
    if (typed !== null) return `The deployment refused this configuration: ${typed}`;
  }
  if (status === 409) {
    // Two distinct causes reach 409 here and the operator's next step differs, so
    // the copy names both rather than guessing: a duplicate name is theirs to fix,
    // a re-home attempt means they are editing through the wrong peer.
    return "That name is already used within this peer, or the interface belongs to another peer.";
  }
  if (status === 400) return "This interface does not belong to the selected peer.";
  if (status === 422) return "The server rejected this configuration. Check the fields above.";
  if (status === 403) return "This session may not change peer configuration.";
  return "Could not save the interface. The change was not applied.";
}
